import { useState } from "react";

import type {
  BuilderLegalStructure,
  BuilderLegalStructureEntityEvidence,
  BuilderLegalStructureSourceDocument,
} from "@/lib/api/databook";

import { LegalStructureEditor } from "./LegalStructureEditor";
import {
  cloneLegalStructure,
  legalStructuresHaveSameWritableState,
} from "./legalStructure";

const DEMO_GROUPS = [
  { id: "prod", name: "Production companies", position: 0 },
  { id: "trade", name: "Trading and distribution", position: 1 },
  { id: "other", name: "Property and other", position: 2 },
];

const DEMO_MONTHS = Array.from({ length: 36 }, (_, index) => {
  const monthIndex = 2022 * 12 + 3 + index;
  return `${Math.floor(monthIndex / 12)}-${String((monthIndex % 12) + 1).padStart(2, "0")}`;
});

function demoSource(
  entityId: string,
  id: string,
  filename: string,
  tableTypes: string[] = [],
  documentTypes: string[] = [],
): BuilderLegalStructureSourceDocument {
  return {
    document_id: `demo-${entityId}-${id}`,
    filename,
    source_type: filename.toLowerCase().endsWith(".pdf") ? "pdf" : "excel",
    sheets: tableTypes.map((tableType) => tableType.replaceAll("_", " ")),
    table_types: tableTypes,
    document_types: documentTypes,
    period_coverage: tableTypes.map((tableType) => ({
      table_type: tableType,
      months: DEMO_MONTHS.map((value) => ({
        value,
        status: "received" as const,
      })),
      years: [],
    })),
  };
}

const DEMO_ENTITY_EVIDENCE: Record<
  string,
  Pick<BuilderLegalStructureEntityEvidence, "aliases" | "source_files">
> = {
  hold: {
    aliases: ["Start Demo Ltd", "StartDemo Limited", "Start Demo Group"],
    source_files: [
      demoSource("hold", "group-accounts", "Start_Demo_Group_Accounts_FY23-FY25.xlsx", ["pnl", "balance_sheet", "eliminations"]),
      demoSource("hold", "trial-balance", "Start Demo Limited Trial Balance FY25.xlsx", ["trial_balance"]),
      demoSource("hold", "statutory", "Start-Demo-Ltd FY24 Financial Statements.pdf", [], ["statutory_accounts"]),
      demoSource("hold", "board-pack", "StartDemo Board Pack Mar-25.pdf", [], ["information_memorandum"]),
    ],
  },
  nz: {
    aliases: ["Start Demo NZ Ltd", "StartDemo NZ Limited", "SDNZ"],
    source_files: [
      demoSource("nz", "management-accounts", "Start_Demo_NZ_Management_Accounts_FY23-FY25.xlsx", ["pnl", "balance_sheet", "cash_flow"]),
      demoSource("nz", "trial-balance", "StartDemo NZ Ltd Trial Balance FY25.xlsx", ["trial_balance"]),
      demoSource("nz", "sales-ledger", "SDNZ Sales Ledger FY25.xlsx", ["ar_by_debtor", "revenue"]),
      demoSource("nz", "inventory", "Start Demo (NZ) Inventory Mar-25.xlsx", ["inventory_aging", "inventory_by_type"]),
      demoSource("nz", "payroll", "Start Demo NZ Payroll Summary FY25.xlsx", ["fte"]),
    ],
  },
  feeds: {
    aliases: ["Start Demo Feeds Ltd", "StartDemo Feeds", "SDF"],
    source_files: [
      demoSource("feeds", "management-accounts", "StartDemo Feeds Management Accounts FY25.xlsx", ["pnl", "balance_sheet"]),
      demoSource("feeds", "trial-balance", "SDF Trial Balance Mar-25.xlsx", ["trial_balance"]),
      demoSource("feeds", "inventory", "Start Demo Feeds Inventory Mar-25.xlsx", ["inventory_aging", "inventory_by_type"]),
      demoSource("feeds", "acquisition", "Start Demo Feeds Acquisition Pack.pdf", [], ["information_memorandum"]),
    ],
  },
  au: {
    aliases: ["Start Demo Australia Pty Limited", "StartDemo AU", "SDAU"],
    source_files: [
      demoSource("au", "management-accounts", "StartDemo AU Management Accounts FY23-FY25.xlsx", ["pnl", "balance_sheet"]),
      demoSource("au", "trial-balance", "SDAU Trial Balance FY25.xlsx", ["trial_balance"]),
      demoSource("au", "sales-ledger", "Start Demo Australia Sales Ledger FY25.xlsx", ["ar_by_debtor", "revenue"]),
      demoSource("au", "payables", "StartDemo AU Supplier Ledger FY25.xlsx", ["ap_by_creditor", "purchases_by_supplier"]),
    ],
  },
  ex: {
    aliases: ["Start Demo Export Ltd", "StartDemo Exports", "SDE"],
    source_files: [
      demoSource("ex", "management-accounts", "StartDemo Exports Management Accounts FY25.xlsx", ["pnl", "balance_sheet"]),
      demoSource("ex", "trial-balance", "SDE Trial Balance Mar-25.xlsx", ["trial_balance"]),
      demoSource("ex", "sales-ledger", "Start Demo Export Sales by Customer FY25.xlsx", ["ar_by_debtor", "revenue"]),
      demoSource("ex", "fx", "StartDemo Exports FX Rates FY25.xlsx", ["fx_rates"]),
    ],
  },
  uk: {
    aliases: ["Start Demo UK Ltd", "StartDemo UK Limited", "SDUK"],
    source_files: [
      demoSource("uk", "management-accounts", "StartDemo UK Management Accounts FY23-FY25.xlsx", ["pnl", "balance_sheet"]),
      demoSource("uk", "trial-balance", "SDUK Trial Balance Dec-24.xlsx", ["trial_balance"]),
      demoSource("uk", "sales-ledger", "Start Demo UK Sales Ledger FY25.xlsx", ["ar_by_debtor", "revenue"]),
      demoSource("uk", "disposal", "StartDemo UK Disposal Completion Accounts.pdf", [], ["statutory_accounts"]),
    ],
  },
  prop: {
    aliases: ["Start Demo Property Ltd", "StartDemo Properties", "SDP"],
    source_files: [
      demoSource("prop", "trial-balance", "StartDemo Property Trial Balance FY25.xlsx", ["trial_balance"]),
      demoSource("prop", "fixed-assets", "SDP Fixed Asset Register Mar-25.xlsx", ["fixed_assets_register"]),
      demoSource("prop", "statutory", "Start Demo Property FY24 Financial Statements.pdf", [], ["statutory_accounts"]),
    ],
  },
};

const DEMO_STRUCTURE: BuilderLegalStructure = {
  schema_version: 2,
  presentation_currency: "USD",
  presentation_currency_confirmed: true,
  groups: DEMO_GROUPS,
  entities: [
    {
      id: "hold",
      name: "Start Demo Limited",
      activity_summary: "Non-trading holding company for the group",
      operating_currency: "USD",
      deal_events: [],
      transaction_perimeter: "inside",
      display_group_id: null,
      position: 0,
      parent_entity_id: null,
      relationship_type: null,
      active: true,
      origin: "source",
      source_names: ["Start Demo Limited", "Start Demo Ltd", "StartDemo Limited"],
    },
    ...[
      ["nz", "Start Demo NZ Limited", "USD", "prod", 0],
      ["feeds", "Start Demo Feeds Limited", "USD", "prod", 1],
      ["au", "Start Demo Australia Pty Ltd", "AUD", "trade", 0],
      ["ex", "Start Demo Exports Limited", "USD", "trade", 1],
      ["uk", "Start Demo UK Limited", "GBP", "trade", 2],
      ["prop", "Start Demo Property Limited", "USD", "other", 0],
    ].map(([id, name, operatingCurrency, groupId, position]) => ({
      id: String(id),
      name: String(name),
      activity_summary: null,
      operating_currency: String(operatingCurrency),
      deal_events: id === "feeds"
        ? [{ type: "acquisition" as const, effective_month: "2024-10" }]
        : id === "uk"
          ? [{ type: "disposal" as const, effective_month: "2025-01" }]
          : [],
      transaction_perimeter: "inside" as const,
      display_group_id: String(groupId),
      position: Number(position),
      parent_entity_id: "hold",
      relationship_type: "consolidates" as const,
      active: true,
      origin: "source" as const,
      source_names: [
        String(name),
        ...(DEMO_ENTITY_EVIDENCE[String(id)]?.aliases ?? []),
      ],
    })),
  ],
};

export function DemoLegalStructureStage({
  onContinue,
  initialValue = DEMO_STRUCTURE,
}: {
  onContinue: () => void;
  initialValue?: BuilderLegalStructure;
}) {
  const [saved, setSaved] = useState(
    () => cloneLegalStructure(initialValue),
  );
  const [draft, setDraft] = useState<BuilderLegalStructure | null>(null);
  const [editing, setEditing] = useState(false);
  const dirty = draft !== null
    && !legalStructuresHaveSameWritableState(draft, saved);
  const evidence = Object.fromEntries(
    initialValue.entities.map((entity) => [
      entity.id,
      {
        aliases: DEMO_ENTITY_EVIDENCE[entity.id]?.aliases ?? [],
        highlights: [],
        included_in_consolidation: false,
        source_currencies: [entity.operating_currency],
        source_files: DEMO_ENTITY_EVIDENCE[entity.id]?.source_files ?? [],
        relationships: [],
      } satisfies BuilderLegalStructureEntityEvidence,
    ]),
  );

  return (
    <LegalStructureEditor
      value={draft ?? saved}
      evidence={evidence}
      consolidationSources={[]}
      consolidationCurrencies={[]}
      analysisInterval={{
        financialYearEndMonth: 3,
        startFiscalYear: 2022,
        endFiscalYear: 2026,
      }}
      editing={editing}
      dirty={dirty}
      canEdit
      onChange={(next) => {
        setDraft(
          !editing && legalStructuresHaveSameWritableState(next, saved)
            ? null
            : next,
        );
      }}
      onEdit={() => {
        setDraft((current) => current ?? cloneLegalStructure(saved));
        setEditing(true);
      }}
      onSave={() => {
        if (draft) setSaved(draft);
        setDraft(null);
        setEditing(false);
      }}
      onConfirmPresentationCurrency={() => {
        setSaved({
          ...(draft ?? saved),
          presentation_currency_confirmed: true,
        });
        setDraft(null);
        setEditing(false);
      }}
      onCancel={() => {
        setDraft(null);
        setEditing(false);
      }}
      onContinue={onContinue}
    />
  );
}
